<template>
  <main
    class="main-content d-flex justify-content-center align-items-start p-4 overflow-auto mt-6"
    style="background-color: #f8f9fa"
  >
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-md-8 col-lg-7">
          <div class="card border-0 shadow-sm mb-4">
            <div class="card-body p-4">
              <h5 class="mb-1">Bank Account</h5>
              <p class="text-muted small mb-4">
                Add your company bank account, account name should be match your
                company name
              </p>

              <div class="form-group">
                <label>Bank<span class="text-danger">*</span></label>
                <select
                  class="form-control"
                  v-model="bankName"
                  :disabled="isLoadingBanks"
                >
                  <option value="" disabled>
                    {{ isLoadingBanks ? "Loading banks..." : "Select Bank" }}
                  </option>
                  <option
                    v-for="bank in bankOptions"
                    :key="bank.value"
                    :value="bank.value"
                  >
                    {{ bank.text }}
                  </option>
                </select>
                <small v-if="errors.bankName" class="text-danger">{{
                  errors.bankName
                }}</small>
              </div>

              <div class="form-group">
                <label>Account Number<span class="text-danger">*</span></label>
                <div class="position-relative">
                  <argon-input
                    type="text"
                    placeholder="Enter Account Number"
                    v-model="accountNumber"
                    @input="validateAccountNumber"
                  />
                  <button
                    class="mt-1 check-button position-absolute"
                    @click="checkAccount"
                  >
                    Check
                  </button>
                </div>
                <small v-if="errors.accountNumber" class="text-danger">{{
                  errors.accountNumber
                }}</small>
                <div v-if="isCheckingAccount" class="mt-2">
                  <div
                    class="spinner-border spinner-border-sm text-primary"
                    role="status"
                  >
                    <span class="sr-only">Loading...</span>
                  </div>
                  <span class="ml-2">Checking account...</span>
                </div>
                <small v-if="errors.checkAccount" class="text-danger">{{
                  errors.checkAccount
                }}</small>
              </div>

              <div class="form-group">
                <label
                  >Bank Account Name<span class="text-danger">*</span></label
                >
                <div v-if="isAccountVerified" class="d-flex align-items-center">
                  <div class="form-control bg-success text-white">
                    {{ verifiedAccountName }}
                  </div>
                </div>
                <div v-else>
                  <argon-input
                    type="text"
                    placeholder="Enter Account Name"
                    v-model="accountName"
                  />
                </div>
                <small v-if="errors.accountName" class="text-danger">{{
                  errors.accountName
                }}</small>
              </div>

              <div class="d-flex justify-content-start mt-4">
                <button
                  class="btn btn-md btn-outline-secondary me-2"
                  @click="$router.push('/company-detail')"
                >
                  Back
                </button>
                <button
                  class="btn btn-md btn-outline-secondary me-2"
                  @click="saveAndQuit"
                >
                  Save & Quit
                </button>
                <button
                  class="btn btn-md btn-primary"
                  @click="showContinueModal"
                >
                  Continue
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </main>
  <ModalSubmit
    v-if="isModalVisible"
    :show="isModalVisible"
    @close="isModalVisible = false"
  />
  <Loading :isLoading="isLoading" />
</template>

<script>
import { onBeforeUnmount, onBeforeMount, computed } from "vue";
import { useStore } from "vuex";
import ArgonInput from "@/components/ArgonInput";
import { buildFormData } from "../../utils/param-builder";
import {
  getDataCompany,
  saveDataCompany,
  checkBankAccount,
  getBank,
} from "../../api/company";
import Loading from "@/components/Loading";
import ModalSubmit from "@/views/company/ModalSubmit";
import { useRoute } from "vue-router";

export default {
  components: {
    ArgonInput,
    Loading,
    ModalSubmit,
  },
  data() {
    return {
      merchantId: 3,
      stepNumber: 3,
      bankName: "",
      accountNumber: "",
      accountName: "",
      isModalVisible: false,
      isLoading: false,
      isLoadingBanks: false,
      isCheckingAccount: false,
      isAccountVerified: false,
      verifiedAccountName: "",
      type: "",
      bankOptions: [],
      errors: {
        bankName: "",
        accountNumber: "",
        accountName: "",
        checkAccount: "",
      },
    };
  },
  watch: {
    bankName() {
      // Reset verification status when bank changes
      this.isAccountVerified = false;
      this.verifiedAccountName = "";
      this.errors.checkAccount = "";
    },
  },
  methods: {
    validateAccountNumber() {
      // Remove any non-numeric characters
      this.accountNumber = this.accountNumber.replace(/\D/g, "");

      // Reset verification status when account number changes
      this.isAccountVerified = false;
      this.verifiedAccountName = "";
      this.errors.checkAccount = "";
    },
    async checkAccount() {
      // Reset errors
      this.errors.bankName = "";
      this.errors.accountNumber = "";
      this.errors.checkAccount = "";

      // Validate inputs first
      if (!this.bankName) {
        this.errors.bankName = "Please select a bank first";
        return;
      }

      if (!this.accountNumber) {
        this.errors.accountNumber = "Please enter an account number";
        return;
      }

      // Start checking
      this.isCheckingAccount = true;

      try {
        const response = await checkBankAccount(
          this.bankName,
          this.accountNumber
        );

        if (response.status === "success" && response.data?.is_valid) {
          this.isAccountVerified = true;
          this.verifiedAccountName = response.data.account_name;
          this.accountName = response.data.account_name; // Also update the accountName field
          this.errors.checkAccount = ""; // Clear any previous errors
        } else {
          this.isAccountVerified = false;
          this.verifiedAccountName = "";
          this.errors.checkAccount =
            response.message || "Account could not be verified";
        }
      } catch (error) {
        console.error("Error checking account:", error);
        this.isAccountVerified = false;
        this.verifiedAccountName = "";
        this.errors.checkAccount =
          error.message || "Failed to check account. Please try again.";
      } finally {
        this.isCheckingAccount = false;
      }
    },
    async fetchBanks() {
      this.isLoadingBanks = true;
      try {
        const response = await getBank();
        if (response.status === "success" && response.data) {
          // Map the bank data to options format
          this.bankOptions = response.data.map((bank) => ({
            value: bank.name || bank.bank_name, // Handle different possible field names
            text: bank.display_name || bank.name || bank.bank_name,
          }));
        } else {
          console.error("Failed to fetch banks:", response);
          // Fallback to hardcoded options if API fails
          this.bankOptions = [
            { value: "BCA", text: "Bank BCA" },
            { value: "JAGO", text: "Bank Jago" },
            { value: "MANDIRI", text: "Bank Mandiri" },
          ];
        }
      } catch (error) {
        console.error("Error fetching banks:", error);
        // Fallback to hardcoded options if API fails
        this.bankOptions = [
          { value: "BCA", text: "Bank BCA" },
          { value: "JAGO", text: "Bank Jago" },
          { value: "MANDIRI", text: "Bank Mandiri" },
        ];
      } finally {
        this.isLoadingBanks = false;
      }
    },
    async fetchData() {
      const data = await getDataCompany(this.merchantId, this.stepNumber);
      if (data.status === "success") {
        this.bankName = data.data.bank_name;
        this.accountNumber = data.data.bank_account_number;
        this.accountName = data.data.bank_account_name;

        // If we already have account data from before, consider it verified
        if (data.data.bank_account_name && data.data.bank_account_number) {
          this.isAccountVerified = data.data.is_account_verified || false;
          if (this.isAccountVerified) {
            this.verifiedAccountName = data.data.bank_account_name;
          }
        }
      }
    },
    async saveInput() {
      if (!this.validate()) {
        return;
      }

      this.isLoading = true;
      const body = {
        merchant_id: this.merchantId,
        step_number: this.stepNumber,
        bank_name: this.bankName,
        bank_account_number: this.accountNumber,
        bank_account_name: this.isAccountVerified
          ? this.verifiedAccountName
          : this.accountName,
        is_account_verified: this.isAccountVerified,
        type: this.type,
      };
      const formData = buildFormData(body);
      try {
        const response = await saveDataCompany(formData, this.stepNumber);
        return response;
      } catch (error) {
        console.error("Error saving data:", error);
        // Show general error under the form
        this.$nextTick(() => {
          this.errors.accountName =
            "An unexpected error occurred. Please try again.";
        });
        return null;
      } finally {
        this.isLoading = false;
      }
    },
    async saveAndQuit() {
      this.type = "save";
      const response = await this.saveInput();
      if (response && response.status === "success") {
        this.$router.push("/home");
      }
    },
    async showContinueModal() {
      this.type = "submit";
      const response = await this.saveInput();
      if (response && response.status === "success") {
        this.isModalVisible = true;
      }
    },
    validate() {
      // Reset all errors
      this.errors.bankName = "";
      this.errors.accountNumber = "";
      this.errors.accountName = "";

      let isValid = true;

      if (!this.bankName) {
        this.errors.bankName = "Bank name is required";
        isValid = false;
      }

      if (!this.accountNumber) {
        this.errors.accountNumber = "Bank account number is required";
        isValid = false;
      }

      if (!this.isAccountVerified && !this.accountName) {
        this.errors.accountName = "Bank account name is required";
        isValid = false;
      }

      return isValid;
    },
  },
  async mounted() {
    await this.fetchBanks();
    this.fetchData();
  },
  setup() {
    const store = useStore();
    const body = document.getElementsByTagName("body")[0];
    const route = useRoute();
    const isBankCompanyPage = computed(() => route.name === "BankCompany");

    onBeforeMount(() => {
      const storedEmail = localStorage.getItem("merchant_email");
      const storedToken = localStorage.getItem("auth_token");
      if (storedEmail) {
        store.commit("auth/setEmail", storedEmail);
      }
      if (storedToken) {
        store.commit("auth/setToken", storedToken);
      }
      store.state.hideConfigButton = true;
      store.state.showSidenav = false;
      store.state.showFooter = false;
      body.classList.remove("bg-gray-100");
    });

    onBeforeUnmount(() => {
      store.state.hideConfigButton = false;
      store.state.showSidenav = true;
      store.state.showFooter = true;
      body.classList.add("bg-gray-100");
    });

    return {
      isBankCompanyPage,
    };
  },
  created() {
    this.merchantId = localStorage.getItem("merchantId");
    this.companyName = localStorage.getItem("merchantName");
  },
};
</script>

<style scoped>
.main-content {
  min-height: 100vh;
  background-color: #f8f9fa;
}

.font-label {
  font-size: 16px;
}

.text-info-input {
  font-size: 8px;
}

.card {
  border-radius: 0.5rem;
}

.card-header {
  border-top-left-radius: 0.5rem !important;
  border-top-right-radius: 0.5rem !important;
}

.input-form-frame {
  border: 1px solid gray;
  border-radius: 20px;
  padding: 16px 4px;
  margin-top: 10px;
}

label {
  white-space: nowrap;
}

.check-button {
  right: 0;
  top: 0;
  bottom: 0;
  background: transparent;
  color: #2e7d32;
  font-weight: 500;
  padding: 0 15px;
  border: none;
  z-index: 10;
}

.check-button:hover {
  color: #1b5e20;
}

.position-relative {
  position: relative;
}

.position-absolute {
  position: absolute;
}
</style>
